// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/machine/MachineListPage.jsx
// 📝 Purpose:
//   Machine Master – List Page
//   - Gradient header
//   - KPI summary bar
//   - Search + status filter
//   - Table view
//   - Create / Edit Drawer
// ======================================================================

import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";

import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import MachineDrawer from "./MachineDrawer";
import MachineKpiBar from "../../../components/machine/MachineKpiBar";

// ----------------------------------------------------------------------
export default function MachineListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  const [machineGroups, setMachineGroups] = useState([]);

  // ------------------------------------------------------------
  // Load machines
  // ------------------------------------------------------------
  const loadMachines = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/machines"
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load machines", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMachineGroups = useCallback(async () => {
  try {
    const res = await axiosClient.get(
      "/api/ppe/masters/machine-groups"
    );
    setMachineGroups(res.data?.data || []);
  } catch (err) {
    console.error("Failed to load machine groups", err);
    setMachineGroups([]);
  }
}, []);

useEffect(() => {
  loadMachines();
  loadMachineGroups();
}, [loadMachines, loadMachineGroups]);

  
  
  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Filtering
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.machine_code?.toLowerCase().includes(q) ||
      r.machine_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active) ||
      (status === "INACTIVE" && !r.is_active);

    return matchesSearch && matchesStatus;
  });
  const machineGroupMap = React.useMemo(() => {
  const map = {};
  machineGroups.forEach((g) => {
    map[g.id] = g.group_name;
  });
  return map;
}, [machineGroups]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Machines</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Manage production machines and capacities
          </div>
        </div>

        {permissions?.MACHINE_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Machine
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <MachineKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadMachines}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 150 }}>Machine Code</th>
                <th>Machine Name</th>
                <th style={{ width: 160 }}>Machine Group</th>
                <th style={{ width: 120 }}>Bottleneck</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-muted"
                  >
                    No machines found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.machine_code}</td>
                  <td>{r.machine_name}</td>
                  <td>{machineGroupMap[r.machine_group_id] || "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_bottleneck
                          ? "bg-danger"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_bottleneck ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {permissions?.MACHINE_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <MachineDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={loadMachines}
      />
    </div>
  );
}
